/**
 * The last few statewide responses this tab received, so coming back to
 * `/map` from a county map shows the results again at once, with no request
 * (a replay is free under the cap but costs ~0.5 s and a MotherDuck query).
 *
 * Module memory only, never storage: it holds owner names and dies with the
 * tab, and sign-out empties it (`useAuth`). Bounded (`MEMORY_SIZE`,
 * `MEMORY_TTL_MS`). `useStatewideSearch` keys each entry by the account, its
 * plan, the worker API version, the order and `criteriaKey` (the same key the
 * daily cap counts), and puts in only successful answers from a worker at
 * this client's API version. StateView unmounts on every hop, so the hook's
 * own state cannot do this.
 *
 * Its own module, with type-only imports, so `useAuth` can clear it without
 * pulling the statewide search into every page.
 */
import type { SortDirection, StatewideCriteria, StatewideResult, StatewideSortKey } from "@/lib/statewide-search";
import type { SearchSuggestion } from "@/lib/statewide-suggest";

export interface RememberedSearch {
  key: string;
  at: number;
  applied: StatewideCriteria;
  results: StatewideResult[];
  totalMatches: number;
  mailingMatches: number;
  /** Free's count of mailing-address matches it was not sent (`lockedMailingMatches`). */
  lockedMailingMatches?: number;
  hasMore: boolean;
  sort: StatewideSortKey;
  dir: SortDirection;
  strippedFilters: string[];
  layerNotes: string[];
  suggestions: SearchSuggestion[];
  /**
   * The quota of the response that counted this search: when its day rolls
   * over and the cap then. Never a later search's: the way back stamps these
   * as the memo's `freeUntil`/`capped`, and a later day's `resetsAt` would
   * call a replay free after the day that counted it had ended.
   */
  dailyLimit: number | null;
  resetsAt: string | null;
}

export interface RememberedQuota {
  userId: string;
  searchesToday: number | null;
  dailyLimit: number | null;
  resetsAt: string | null;
}

const MEMORY_SIZE = 3;
const MEMORY_TTL_MS = 30 * 60 * 1000;
let remembered: RememberedSearch[] = [];
/** The latest quota the worker reported, per account: newer than any remembered search's. */
let lastQuota: RememberedQuota | null = null;

export function recallSearch(key: string, now: number): RememberedSearch | null {
  remembered = remembered.filter((entry) => now - entry.at < MEMORY_TTL_MS);
  return remembered.find((entry) => entry.key === key) ?? null;
}

export function rememberSearch(entry: RememberedSearch): void {
  remembered = [entry, ...remembered.filter((other) => other.key !== entry.key)].slice(0, MEMORY_SIZE);
}

/** The entry for `key` as it is now, to fill in what arrives after it (suggestions). */
export function rememberedEntry(key: string): RememberedSearch | undefined {
  return remembered.find((other) => other.key === key);
}

export function rememberQuota(quota: RememberedQuota): void {
  lastQuota = quota;
}

export function lastQuotaFor(userId: string): RememberedQuota | null {
  return lastQuota?.userId === userId ? lastQuota : null;
}

/** Forget every remembered search and quota: on sign-out, and between tests. */
export function forgetStatewideSearches(): void {
  remembered = [];
  lastQuota = null;
}
